import React, { useState, useEffect, useMemo, useRef } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import useScoFormsEnabled from "../../hooks/useScoFormsEnabled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import toast from "react-hot-toast";
import Breadcrumbs from "../../components/Breadcrumbs";
import {
  getAccountTypesByAgreement,
  getUserProgramsByAgreement,
} from "../../api/Agreement";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups";
import {
  getUsersByTenant,
  createUserHardwareFormSubmit,
  getScoFormSettings,
} from "../../api/Users";
import CreateUserForm from "../../components/user/CreateUserForm";
import { isIsoDate } from "../../components/DateField";
import {
  effectiveDialCode,
  phoneNumberError,
  withDialCode,
} from "../../components/user/phoneNumber";
import { getScoCustomQuestions, getScoFormValues, getTenant } from "../../api/Tenants";
import { offeredListValues } from "../../components/user/listValues";
import {
  customerEmailDomains,
  effectiveEmailDomain,
  joinEmail,
} from "../../components/user/emailAddress";
import { getAddOnLicenseOptionsByTenant } from "../../api/Licenses";
import {
  getGroupOptionsByTenant,
  getUserGroupMemberships,
} from "../../api/EntraGroups";
import { getHardwareAssortment, getScoDevicesByTenant } from "../../api/Hardware";
import {
  EMPTY_HARDWARE_ORDER,
  EMPTY_EXISTING_COMPUTER,
  buildHardwareFormData,
  validateHardware,
} from "../../components/hardware/hardwareFormData";

/**
 * Fallback SCO form settings, used only if the settings endpoint cannot be reached.
 * Mirrors the requirement spec's defaults so the form stays usable.
 */
const FALLBACK_SCO_FORM_SETTINGS = {
  activationLeadTimeDays: 14,
  enforceActivationLeadTime: false,
  activationDateHelpText: "",
  allowCustomJobTitle: true,
  allowCustomCountry: false,
  offices: [],
  countries: [],
  companies: [],
  supportsHardwareOrder: false,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Fields whose validation message shows while typing rather than after a submit attempt. */
const IMMEDIATE_ERROR_FIELDS = ["activationDate", "mobilePhone", "phone"];

const DATE_FORMAT_ERROR = "Enter the date as yyyy-mm-dd.";

/** Characters in a finished yyyy-mm-dd date; a shorter typed value is still being written. */
const FULL_DATE_LENGTH = 10;

/**
 * Shifts an ISO date (yyyy-MM-dd) by a number of days without letting the browser's
 * timezone move the result to the neighbouring day.
 *
 * @param {string} isoDate - Date in yyyy-MM-dd form
 * @param {number} days - Days to add; negative shifts backwards
 * @returns {string} The shifted date in yyyy-MM-dd form, or "" if the input is unusable
 */
const shiftIsoDate = (isoDate, days) => {
  if (!isoDate) return "";

  const parsed = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return "";

  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
};

/**
 * Collects the distinct non-empty values of one field across the customer's users, so
 * office, country and title can offer the values already in use (PBI #292).
 *
 * @param {object[]} users - The customer's users
 * @param {string} field - The field to collect
 * @returns {string[]} Sorted distinct values
 */
const distinctValues = (users, field) => {
  const values = users
    .map((user) => user[field])
    .filter((value) => typeof value === "string" && value.trim() !== "");

  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
};

/**
 * #352: meeting rooms, resources and shared mailboxes are ordinary Entra accounts without
 * licences, and their Office field often holds just the room's name. The API sends an empty
 * licence map for an unlicensed account when the tenant has licence data, and no map at all
 * when it has none (a customer whose Microsoft 365 Diflex does not manage) — there nothing can
 * be told apart, so nothing is left out.
 *
 * @param {object} user - A user as returned by the users-by-tenant endpoint
 * @returns {boolean} True for an account that should not contribute an office
 */
const isUnlicensedAccount = (user) =>
  !!user?.licenses && Object.keys(user.licenses).length === 0;

/**
 * Combines the values configured for the customer with the ones its users already use.
 *
 * Country is constrained to the company's values, so the values in use cannot be the only
 * source: a country that is valid but not yet assigned to anyone would then be unreachable.
 * Configured values come first, the ones merely in use after.
 *
 * @param {string[]} configured - Values from the SCO form settings
 * @param {string[]} inUse - Values collected from the customer's users
 * @returns {string[]} The combined list, without duplicates
 */
const combineOptions = (configured, inUse) => [
  ...new Set([...(configured || []), ...inUse]),
];

/** Labels an existing user as "Name (email)" so the right person can be identified. */
const existingUserLabel = (user) =>
  user.email ? `${user.displayName} (${user.email})` : user.displayName;

const CreateUserPage = () => {
  const { agreementPublicId, tenantPublicId } = useParams();
  const { enabled, loaded } = useScoFormsEnabled(agreementPublicId);
  const { getAccessToken } = useAuth();

  const [accountTypes, setAccountTypes] = useState([]);
  const [accountTypesLoaded, setAccountTypesLoaded] = useState(false);
  const [billingGroups, setBillingGroups] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [users, setUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);

  // Feature #301: the hardware part of the form. The choice decides which section shows; the
  // assortment and the device inventory are loaded on demand for the chosen section.
  const [hardwareChoice, setHardwareChoice] = useState("none");
  const [hardwareOrder, setHardwareOrder] = useState(EMPTY_HARDWARE_ORDER);
  const [existingComputer, setExistingComputer] = useState(EMPTY_EXISTING_COMPUTER);
  const [hardwareProducts, setHardwareProducts] = useState([]);
  const [hardwareProductsLoading, setHardwareProductsLoading] = useState(false);
  const [scoDevices, setScoDevices] = useState([]);
  const [scoDevicesLoading, setScoDevicesLoading] = useState(false);

  // PBI #320: the customer's own questions and the orderer's answers, keyed by
  // question public id. The answers travel inside formData; the API resolves the
  // labels server-side when it builds the case mail.
  const [customQuestions, setCustomQuestions] = useState([]);
  // #359: the tenant's own domain is the first email domain offered.
  const [tenant, setTenant] = useState(null);
  // #357/#358: the customer's saved list values and addresses.
  const [scoFormValues, setScoFormValues] = useState({ listValues: [], addresses: [] });
  const [customAnswers, setCustomAnswers] = useState({});
  const [userPrograms, setUserPrograms] = useState([]);
  const [userProgramsLoaded, setUserProgramsLoaded] = useState(false);

  // PBI #300: the customer's own licences, offered as add-ons for the new user.
  const [addOnLicenses, setAddOnLicenses] = useState([]);

  // PBI #297: the customer's own Entra groups, plus the status and age of the last sync.
  // The status is carried, not just the list: only it can tell a customer with no groups
  // from one whose administrator has not approved Insight's access to read them.
  const [groupOptions, setGroupOptions] = useState(null);
  const [groupMembershipsLoading, setGroupMembershipsLoading] = useState(false);
  // The colleague whose memberships are currently being read, so a stale answer can be
  // recognised and dropped. A ref, not state: it is read inside an in-flight callback.
  const groupModelRequestRef = useRef(null);

  // PBI #292/#293: policies and the activation lead time are resolved server-side so the
  // open decisions #324 and #325 can be answered without a release.
  const [scoFormSettings, setScoFormSettings] = useState(
    FALLBACK_SCO_FORM_SETTINGS
  );

  // The pre-filled activation date must not overwrite a date the orderer chose.
  const [activationDateTouched, setActivationDateTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [user, setUser] = useState({
    tenantId: tenantPublicId,
    firstName: "",
    lastName: "",
    jobTitle: "",
    emailLocalPart: "",
    emailDomain: "",
    mobilePhone: "",
    phone: "",
    // #358: a saved address chosen by its label, or a new one typed in.
    addressChoice: "",
    streetAddress: "",
    postalCode: "",
    city: "",
    accountTypeName: "",
    accountTypeId: "",
    office: "",
    country: "",
    company: "",
    department: "",
    comment: "",
    manager: "",
    details: "",
    startDate: "",
    activationDate: "",
    billingGroupName: "",
    billingGroupPublicId: "",
    // PBI #300: public ids of the add-on licences chosen for this user.
    addOnLicensePublicIds: [],
    // PBI #297: the group memberships requested for this user, and the colleague they
    // were copied from. The API resolves the names and works out what was left out.
    entraGroupPublicIds: [],
    groupModelUserPublicId: "",
    groupModelUserLabel: "",
    // #298: free text — the mailboxes Diflex should grant access to by hand.
    sharedMailboxes: "",
  });

  const navigate = useNavigate();

  const [originalUser, setOriginalUser] = useState(user);

  useEffect(() => {
    if (user && originalUser) {
      const isEqual = JSON.stringify(user) === JSON.stringify(originalUser);
      setHasChanges(!isEqual);
    }
  }, [user, originalUser]);

  const jobTitleOptions = useMemo(
    () => distinctValues(users, "jobTitle"),
    [users]
  );
  // #352: rooms and shared mailboxes carry junk in these fields, so only licensed accounts
  // contribute the values in use.
  const licensedUsers = useMemo(
    () => users.filter((tenantUser) => !isUnlicensedAccount(tenantUser)),
    [users]
  );
  const officeOptions = useMemo(
    () =>
      offeredListValues(
        "Office",
        scoFormValues.listValues,
        licensedUsers,
        "officeLocation",
        scoFormSettings.offices
      ),
    [scoFormValues, licensedUsers, scoFormSettings]
  );
  const countryOptions = useMemo(
    () =>
      combineOptions(
        scoFormSettings.countries,
        distinctValues(users, "country")
      ),
    [users, scoFormSettings]
  );
  const companyOptions = useMemo(
    () =>
      offeredListValues(
        "Company",
        scoFormValues.listValues,
        licensedUsers,
        "company",
        scoFormSettings.companies
      ),
    [scoFormValues, licensedUsers, scoFormSettings]
  );
  const departmentOptions = useMemo(
    () =>
      offeredListValues("Department", scoFormValues.listValues, licensedUsers, "department"),
    [scoFormValues, licensedUsers]
  );
  // #359: the same accounts that may name an office may name a domain.
  const emailDomainOptions = useMemo(
    () => customerEmailDomains(tenant?.domain, licensedUsers),
    [tenant, licensedUsers]
  );
  const newUserEmail = joinEmail(
    user.emailLocalPart,
    effectiveEmailDomain(user.emailDomain, emailDomainOptions)
  );
  const existingUserLabels = useMemo(
    () => users.map(existingUserLabel).filter(Boolean),
    [users]
  );

  /**
   * Validates the form. Returns a map of field name to message; an empty map means the
   * form may be submitted.
   */
  const errors = useMemo(() => {
    const found = {};
    const leadTimeDays = scoFormSettings.activationLeadTimeDays;

    if (!user.firstName?.trim()) found.firstName = "First name is required.";
    if (!user.lastName?.trim()) found.lastName = "Last name is required.";

    if (!user.emailLocalPart?.trim()) {
      found.email = "Primary email address is required.";
    } else if (user.emailLocalPart.includes("@")) {
      found.email = "Enter only the part before @ — choose the domain next to it.";
    } else if (!effectiveEmailDomain(user.emailDomain, emailDomainOptions)) {
      found.email = "No email domain is registered for this customer. Contact Diflex.";
    } else if (!EMAIL_PATTERN.test(newUserEmail)) {
      found.email = "Enter a valid email address.";
    } else {
      const taken = users.some(
        (tenantUser) => tenantUser.email?.toLowerCase() === newUserEmail.toLowerCase()
      );
      if (taken) found.email = "A user with this email already exists.";
    }

    const mobilePhoneError = phoneNumberError(
      user.mobilePhone,
      effectiveDialCode(user.mobilePhoneCountryCode, user.country)
    );
    if (mobilePhoneError) found.mobilePhone = mobilePhoneError;

    const phoneError = phoneNumberError(
      user.phone,
      effectiveDialCode(user.phoneCountryCode, user.country)
    );
    if (phoneError) found.phone = phoneError;

    // #358: the address the email signature needs, required for every new user.
    if (scoFormValues.addresses.length > 0 && !user.addressChoice) {
      found.address = "Choose an address, or add a new one.";
    } else if (!user.streetAddress?.trim() || !user.postalCode?.trim() || !user.city?.trim()) {
      found.address = "Street address, postal code and city are all required.";
    }

    if (!user.accountTypePublicId) found.accountType = "Account type is required.";
    if (!user.billingGroupPublicId) found.billingGroup = "Billing group is required.";
    if (!user.startDate) found.startDate = "Start date is required.";
    else if (!isIsoDate(user.startDate)) found.startDate = DATE_FORMAT_ERROR;

    if (!user.activationDate) {
      found.activationDate = "Activation date is required.";
    } else if (!isIsoDate(user.activationDate)) {
      found.activationDate = DATE_FORMAT_ERROR;
    } else if (isIsoDate(user.startDate)) {
      if (user.activationDate > user.startDate) {
        found.activationDate =
          "The activation date must be on or before the start date.";
      } else if (
        scoFormSettings.enforceActivationLeadTime &&
        user.activationDate > shiftIsoDate(user.startDate, -leadTimeDays)
      ) {
        found.activationDate = `The account must be activated at least ${leadTimeDays} days before the start date.`;
      }
    }

    const hardwareError = validateHardware(hardwareChoice, hardwareOrder);
    if (hardwareError) found.hardware = hardwareError;

    return found;
  }, [
    user,
    users,
    scoFormSettings,
    hardwareChoice,
    hardwareOrder,
    emailDomainOptions,
    newUserEmail,
    scoFormValues,
  ]);

  // Rules about what was typed report as soon as the field has a value — the hard 14-day
  // activation rule (#362) and the phone format (#360). Everything else, "required" included,
  // waits for a submit attempt so the orderer is not nagged about fields not reached yet. A
  // typed date is only judged once all of yyyy-mm-dd is there; until then it is unfinished,
  // not wrong.
  const isUnfinishedDate = (field) =>
    errors[field] === DATE_FORMAT_ERROR && String(user[field]).length < FULL_DATE_LENGTH;

  const visibleErrors = submitAttempted
    ? errors
    : Object.fromEntries(
        IMMEDIATE_ERROR_FIELDS.filter(
          (field) => user[field] && errors[field] && !isUnfinishedDate(field)
        ).map((field) => [field, errors[field]])
      );

  useEffect(() => {
    fetchUsers();
    fetchAccountTypes();
    fetchBillingGroups();
    fetchUserPrograms();
    fetchScoFormSettings();
    fetchCustomQuestions();
    fetchAddOnLicenses();
    fetchGroupOptions();
    fetchTenant();
    fetchScoFormValues();
  }, [agreementPublicId, tenantPublicId]);

  // The assortment depends on the chosen billing group, so it is reloaded when that changes.
  useEffect(() => {
    if (hardwareChoice !== "new" || !scoFormSettings.supportsHardwareOrder) return;

    let cancelled = false;
    const load = async () => {
      setHardwareProductsLoading(true);
      try {
        const accessToken = await getAccessToken();
        const products = await getHardwareAssortment(
          agreementPublicId,
          user.billingGroupPublicId || null,
          accessToken
        );
        if (!cancelled) setHardwareProducts(products || []);
      } catch (error) {
        if (!cancelled) {
          setHardwareProducts([]);
          toast.error("Could not load the hardware assortment");
        }
      } finally {
        if (!cancelled) setHardwareProductsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [hardwareChoice, user.billingGroupPublicId, agreementPublicId, scoFormSettings.supportsHardwareOrder]);

  useEffect(() => {
    if (hardwareChoice !== "existing" || !scoFormSettings.supportsHardwareOrder) return;

    let cancelled = false;
    const load = async () => {
      setScoDevicesLoading(true);
      try {
        const accessToken = await getAccessToken();
        const devices = await getScoDevicesByTenant(tenantPublicId, accessToken);
        if (!cancelled) setScoDevices(devices || []);
      } catch (error) {
        // No inventory (customers without Intune) is a normal case: the form falls back to a
        // typed serial number.
        if (!cancelled) setScoDevices([]);
      } finally {
        if (!cancelled) setScoDevicesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [hardwareChoice, tenantPublicId, scoFormSettings.supportsHardwareOrder]);

  const handleHardwareChoiceChange = (choice) => {
    setHardwareChoice(choice);
    setHasChanges(true);
  };

  const handleHardwareOrderChange = (order) => {
    setHardwareOrder(order);
    setHasChanges(true);
  };

  const handleExistingComputerChange = (value) => {
    setExistingComputer(value);
    setHasChanges(true);
  };

  const fetchUsers = async () => {
    try {
      const accessToken = await getAccessToken();
      const tenantUsers = await getUsersByTenant(tenantPublicId, accessToken);

      setUsers(tenantUsers);
      setUsersLoaded(true);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsersLoaded(true);
    }
  };

  const fetchAccountTypes = async () => {
    try {
      const accessToken = await getAccessToken();
      const aT = await getAccountTypesByAgreement(
        agreementPublicId,
        accessToken
      );

      setAccountTypes(aT);
      setAccountTypesLoaded(true);
    } catch (error) {
      console.error("Failed to fetch account types:", error);
      setAccountTypesLoaded(true);
    }
  };

  const fetchUserPrograms = async () => {
    try {
      const accessToken = await getAccessToken();
      const uP = await getUserProgramsByAgreement(
        agreementPublicId,
        accessToken
      );

      setUserPrograms(uP);
      setUserProgramsLoaded(true);
    } catch (error) {
      console.error("Failed to fetch user programs:", error);
      setUserProgramsLoaded(true);
    }
  };

  const fetchScoFormSettings = async () => {
    try {
      const accessToken = await getAccessToken();
      const settings = await getScoFormSettings(accessToken);

      // Keep the fallback for anything the API leaves out, so a partially configured
      // section cannot blank out the date logic.
      setScoFormSettings({ ...FALLBACK_SCO_FORM_SETTINGS, ...settings });
    } catch (error) {
      console.error("Failed to fetch SCO form settings:", error);
    }
  };

  // PBI #320: a failed fetch means no questions are shown — never a blocked form.
  const fetchCustomQuestions = async () => {
    try {
      const accessToken = await getAccessToken();
      const questions = await getScoCustomQuestions(tenantPublicId, accessToken);

      setCustomQuestions(questions || []);
    } catch (error) {
      console.error("Failed to fetch SCO custom questions:", error?.message ?? error);
    }
  };

  // #357/#358: without the saved values the form still offers what the users carry, and
  // asks for a new address.
  const fetchScoFormValues = async () => {
    try {
      const accessToken = await getAccessToken();
      const values = await getScoFormValues(tenantPublicId, accessToken);

      setScoFormValues({
        listValues: values?.listValues || [],
        addresses: values?.addresses || [],
      });
    } catch (error) {
      console.error("Failed to fetch SCO form values:", error?.message ?? error);
    }
  };

  // #359: without the tenant the domains its users already use are still offered.
  const fetchTenant = async () => {
    try {
      const accessToken = await getAccessToken();
      setTenant((await getTenant(tenantPublicId, accessToken)) || null);
    } catch (error) {
      console.error("Failed to fetch tenant:", error?.message ?? error);
    }
  };

  // PBI #300: the add-on picker is an extra. A failed fetch leaves it out of the form
  // instead of blocking onboarding, exactly like the custom questions above.
  const fetchAddOnLicenses = async () => {
    try {
      const accessToken = await getAccessToken();
      const options = await getAddOnLicenseOptionsByTenant(
        tenantPublicId,
        accessToken
      );

      setAddOnLicenses(options || []);
    } catch (error) {
      console.error(
        "Failed to fetch add-on license options:",
        error?.message ?? error
      );
      setAddOnLicenses([]);
    }
  };

  // PBI #297: the picker is an extra, exactly like the add-on licences above. A failed
  // fetch leaves it out of the form rather than blocking onboarding — and note that a
  // customer whose administrator has not approved access is NOT this case: that answers
  // successfully with a status the picker has words for.
  const fetchGroupOptions = async () => {
    try {
      const accessToken = await getAccessToken();
      const options = await getGroupOptionsByTenant(tenantPublicId, accessToken);

      setGroupOptions(options || null);
    } catch (error) {
      console.error(
        "Failed to fetch Entra group options:",
        error?.message ?? error
      );
      setGroupOptions(null);
    }
  };

  /**
   * PBI #297: reads one colleague's memberships and ticks them, limited to the groups
   * the picker actually offers — a membership outside that list (a dynamic group, say)
   * would otherwise be submitted without the orderer ever having seen it.
   *
   * The ref check is what makes rapid re-picking safe. Two reads can be in flight at
   * once, and the slower one may be for a colleague the orderer has already moved on
   * from; letting it land would leave the case mail naming one person and carrying
   * another's groups, which is precisely the mistake a person acting on that mail
   * cannot catch.
   *
   * A failed read leaves the ticks as they are: the orderer can still pick by hand.
   */
  const copyGroupsFromUser = async (modelUserPublicId) => {
    try {
      const accessToken = await getAccessToken();
      const memberships = await getUserGroupMemberships(
        modelUserPublicId,
        accessToken
      );

      if (groupModelRequestRef.current !== modelUserPublicId) return;

      // Read here rather than before the await, so a list refreshed while the read was
      // in flight is the one the ticks are filtered against.
      const offered = new Set(
        (groupOptions?.groups || []).map((group) => group.entraGroupPublicId)
      );

      setUser((prevUser) => ({
        ...prevUser,
        entraGroupPublicIds: (memberships?.entraGroupPublicIds || []).filter((id) =>
          offered.has(id)
        ),
      }));
    } catch (error) {
      console.error(
        "Failed to fetch group memberships:",
        error?.message ?? error
      );
    } finally {
      if (groupModelRequestRef.current === modelUserPublicId)
        setGroupMembershipsLoading(false);
    }
  };

  /** PBI #297: picking the colleague to copy from ticks that person's groups. */
  const handleGroupModelUserChange = (selectedLabel) => {
    const selectedUser = users.find(
      (tenantUser) => existingUserLabel(tenantUser) === selectedLabel
    );
    if (!selectedUser) return;

    groupModelRequestRef.current = selectedUser.userPublicId;

    setUser((prevUser) => ({
      ...prevUser,
      groupModelUserPublicId: selectedUser.userPublicId,
      groupModelUserLabel: selectedLabel,
    }));
    setHasChanges(true);
    setGroupMembershipsLoading(true);

    copyGroupsFromUser(selectedUser.userPublicId);
  };

  const handleCustomAnswerChange = (questionPublicId, value) => {
    setCustomAnswers((prev) => ({ ...prev, [questionPublicId]: value }));
    setHasChanges(true);
  };

  const fetchBillingGroups = async () => {
    try {
      const accessToken = await getAccessToken();
      const groups = await getBillingGroupsByAgreement(
        agreementPublicId,
        accessToken
      );
      setBillingGroups(groups);
    } catch (error) {
      console.error("Failed to fetch billing groups:", error);
    }
  };

  const handleBillingGroupChange = (selectedName) => {
    const selectedGroup = billingGroups.find(
      (group) => group.name === selectedName
    );
    if (!selectedGroup) return;

    setUser((prevUser) => ({
      ...prevUser,
      billingGroupName: selectedGroup.name,
      billingGroupPublicId: selectedGroup.billingGroupPublicId,
    }));
    // The assortment is per billing group, so chosen products may not exist in the new one.
    // Dropping them keeps the order in step with what the orderer can see.
    setHardwareOrder((prev) => ({ ...prev, items: {} }));
    setHasChanges(true);
  };

  const handleAccountTypeChange = (selectedName) => {
    const selectedType = accountTypes.find(
      (type) => type.name === selectedName
    );
    if (!selectedType) return;

    // #363: the new account type's own licence leaves the add-on picker, so a ticked
    // add-on that now matches it is dropped too — otherwise it would stay in the order
    // with no way to see or untick it, and the case mail would list the licence twice.
    const includedMpn = selectedType.licenseMpn?.toLowerCase();
    const includedIds = includedMpn
      ? addOnLicenses
          .filter((license) => license?.mpn?.toLowerCase() === includedMpn)
          .map((license) => license.licensePublicId)
      : [];

    setUser((prevUser) => ({
      ...prevUser,
      accountTypeName: selectedType.name,
      accountTypePublicId: selectedType.accountTypePublicId,
      ...(includedIds.length > 0 && {
        addOnLicensePublicIds: (prevUser.addOnLicensePublicIds || []).filter(
          (id) => !includedIds.includes(id)
        ),
      }),
    }));
    setHasChanges(true);
  };

  const handleManagerChange = (selectedName) => {
    const selectedUser = users.find(
      (tenantUser) => tenantUser.displayName === selectedName
    );
    if (!selectedUser) return;

    setUser((prevUser) => ({
      ...prevUser,
      managerName: selectedUser.displayName,
      managerPublicId: selectedUser.userPublicId,
    }));
    setHasChanges(true);
  };

  /**
   * PBI #293: the activation date is pre-filled to the configured lead time before the
   * start date. A date the orderer has already chosen is left alone.
   */
  const handleStartDateChange = (startDate) => {
    setUser((prevUser) => ({
      ...prevUser,
      startDate,
      activationDate: activationDateTouched
        ? prevUser.activationDate
        : shiftIsoDate(startDate, -scoFormSettings.activationLeadTimeDays),
    }));
    setHasChanges(true);
  };

  const handleActivationDateChange = (activationDate) => {
    setActivationDateTouched(true);
    setUser((prevUser) => ({ ...prevUser, activationDate }));
    setHasChanges(true);
  };

  const handleCreateUser = async () => {
    // The ref is what actually blocks the second click, not the state: React batches state
    // updates, so two clicks in the same tick would both still see isSubmitting as false.
    // The state only drives the button. Submitting twice is no longer just a duplicate
    // email - the backend can create two accounts and draw two licences from the customer's
    // pool, because both requests get past the "does this address already exist" check
    // before either of them has committed.
    if (isSubmittingRef.current) return;

    setSubmitAttempted(true);

    const errorMessages = Object.values(errors);
    if (errorMessages.length > 0) {
      toast.error(errorMessages[0]);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        toast.error("Authorization failed. Please log in again.");
        return;
      }

      const formData = {
        tenantId: tenantPublicId,
        firstName: user.firstName,
        lastName: user.lastName,
        jobTitle: user.jobTitle,
        email: newUserEmail,
        // #360: sent with the country code, e.g. "+46 733 12 34 56".
        mobilePhone: withDialCode(
          effectiveDialCode(user.mobilePhoneCountryCode, user.country),
          user.mobilePhone
        ),
        phone: withDialCode(effectiveDialCode(user.phoneCountryCode, user.country), user.phone),
        accountTypePublicId: user.accountTypePublicId,
        office: user.office,
        country: user.country,
        // #358: a saved address and a new one travel the same way.
        streetAddress: user.streetAddress,
        postalCode: user.postalCode,
        city: user.city,
        company: user.company,
        department: user.department,
        managerPublicId: user.managerPublicId,
        billingGroupPublicId: user.billingGroupPublicId,
        startDate: user.startDate,
        activationDate: user.activationDate,
        details: user.details,
        comment: user.comment,
        userPrograms: user.userPrograms,
        addOnLicensePublicIds: user.addOnLicensePublicIds || [],

        // PBI #297: the ticked groups and the colleague they were copied from. The
        // "do not assign" list in the case mail is computed server-side from those two —
        // never sent from here, so the client cannot decide what Diflex is told was held back.
        entraGroupPublicIds: user.entraGroupPublicIds || [],
        groupModelUserPublicId: user.groupModelUserPublicId || "",

        // #298: free text, granted by hand — the API only carries it to the case mail.
        sharedMailboxes: user.sharedMailboxes || "",
        newUser: true,

        // Feature #301: hardware choice, order lines by product public id and quantity, or the
        // existing computer. Names and prices are resolved server-side against the assortment.
        ...(scoFormSettings.supportsHardwareOrder
          ? buildHardwareFormData(hardwareChoice, hardwareOrder, existingComputer)
          : {}),

        // PBI #320: answers keyed by question public id, raw values (boolean for a
        // checkbox). Blank text answers are omitted — the API renders unanswered
        // questions as "-" so Diflex can tell "not asked" from "left blank".
        customQuestions: Object.fromEntries(
          Object.entries(customAnswers).filter(
            ([, value]) => value !== "" && value !== null && value !== undefined
          )
        ),
      };

      const payload = { source: "Dip", formData: formData };

      await createUserHardwareFormSubmit(payload, accessToken);

      toast.success("User created successfully", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
    } catch (error) {
      console.error("Failed to create user:", error);
      toast.error("Failed to create user");
      return;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
    navigate(-1);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loaded && !enabled) {
    return <Navigate to={`/${agreementPublicId}/${tenantPublicId}/users`} replace />;
  }

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="page-content">
            <Breadcrumbs />
            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">Create User</h2>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <CreateUserForm
                user={user}
                setUser={setUser}
                setHasChanges={setHasChanges}
                billingGroups={billingGroups}
                accountTypes={accountTypes}
                onBillingGroupChange={handleBillingGroupChange}
                onAccountTypeChange={handleAccountTypeChange}
                users={users}
                onManagerChange={handleManagerChange}
                hardware={{
                  supported: !!scoFormSettings.supportsHardwareOrder,
                  choice: hardwareChoice,
                  onChoiceChange: handleHardwareChoiceChange,
                  products: hardwareProducts,
                  productsLoading: hardwareProductsLoading,
                  order: hardwareOrder,
                  onOrderChange: handleHardwareOrderChange,
                  devices: scoDevices,
                  devicesLoading: scoDevicesLoading,
                  existingComputer,
                  onExistingComputerChange: handleExistingComputerChange,
                }}
                userPrograms={userPrograms}
                addOnLicenses={addOnLicenses}
                groups={{
                  status: groupOptions?.status,
                  asOfUtc: groupOptions?.asOfUtc,
                  options: groupOptions?.groups,
                  userLabels: existingUserLabels,
                  onModelUserChange: handleGroupModelUserChange,
                  membershipsLoading: groupMembershipsLoading,
                }}
                scoFormSettings={scoFormSettings}
                jobTitleOptions={jobTitleOptions}
                officeOptions={officeOptions}
                departmentOptions={departmentOptions}
                addresses={scoFormValues.addresses}
                emailDomainOptions={emailDomainOptions}
                countryOptions={countryOptions}
                companyOptions={companyOptions}
                onStartDateChange={handleStartDateChange}
                onActivationDateChange={handleActivationDateChange}
                errors={visibleErrors}
                customQuestions={customQuestions}
                customAnswers={customAnswers}
                onCustomAnswerChange={handleCustomAnswerChange}
              />
              <div className="flex justify-end gap-4 pt-4">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges || isSubmitting}
                  onClick={handleCreateUser}
                >
                  {isSubmitting ? "Saving..." : "Save user"}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default CreateUserPage;
